// Destination flight-time resolver — shared between the React SPA
// (src/components/App.jsx) and the SSR template (src/pages/[...slug].astro) so
// the dest static hero can render the SAME "~X min" / "Hub" / "—" label React
// shows, flash-free. Stage-1 extraction mirroring src/lib/route-resolvers.js:
// these were inline in App.jsx and closed over App-internal runtime globals
// (FLEET, LOCATION_DB, SEO_ROUTES); they are extracted here as PURE functions
// that take the runtime data as parameters. App.jsx passes its CMS-built
// FLEET/LOCATION_DB/SEO_ROUTES; the SSR template will pass its Sanity-fetched
// equivalents. No behaviour change — the bodies are byte-for-byte the originals,
// only the free variables became args.
//
// Server-importable: pure number/string ops, no React, no browser API. The
// haversine/speed primitives are reused from flightTime.js (their documented
// home, also imported by App.jsx).
//
// NOTE on cityLocation: this replicates App.jsx's cityLocation EXACTLY — the
// primary-location pick does NOT require lat/lng (it selects helipad > airport >
// first enabled by city, THEN calcCityFlightMin bails if the pick lacks coords).
// This deliberately differs from flightTime.js's cityCoords (which filters out
// coord-less locations during selection); reusing cityCoords could change which
// location is picked, so it is NOT used here.

import { haversineKm, parseSpeed } from './flightTime.js';

// Primary location (helipad/airport) for a city name — byte-identical to
// App.jsx cityLocation, with the LOCATION_DB passed as `locations`.
export function cityLocation(locations, cityName) {
  const locs = (locations || []).filter(l => l.enabled !== false && l.city === cityName);
  return locs.find(l => l.type === "helipad") || locs.find(l => l.type === "airport") || locs[0] || null;
}

// City-aware flight time (minutes) for one aircraft — byte-identical to App.jsx
// calcFlightMin, with FLEET/LOCATION_DB passed as `fleet`/`locations`.
export function calcCityFlightMin(locations, fleet, fromCity, toCity, aircraftId) {
  const fromLoc = cityLocation(locations, fromCity);
  const toLoc = cityLocation(locations, toCity);
  if (!fromLoc?.lat || !toLoc?.lat) return null;
  const ac = (fleet || []).find(f => f.id === aircraftId);
  const speed = ac ? parseSpeed(ac.speed) : 0;
  if (!speed) return null;
  const dist = haversineKm(fromLoc, toLoc);
  if (dist == null) return null;
  // Pure cruise — no hardcoded taxi/approach overhead (removed 2026-06-05).
  return Math.round((dist / speed) * 60);
}

// Approximate flight time for a route — fastest aircraft, or CMS route fallback.
// Byte-identical to App.jsx getApproxFlightMin, with FLEET/LOCATION_DB/SEO_ROUTES
// passed via opts.
export function getApproxFlightMin(fromCity, toCity, { fleet = [], locations = [], routes = [] } = {}) {
  const times = (fleet || []).map(f => calcCityFlightMin(locations, fleet, fromCity, toCity, f.id)).filter(Boolean);
  if (times.length) return Math.min(...times);
  // Fallback: use CMS flightMin from route
  const route = (routes || []).find(r => r.from === fromCity && r.to === toCity);
  return route?.flightMin || null;
}

// Destination flight-time label: "~X min" / "Hub" / "—" — byte-identical to
// App.jsx destFlightLabel (manual dest.time > fleet calc > dash, Hub edge-case).
// opts carries FLEET/LOCATION_DB/SEO_ROUTES for the calc fallback.
export function destFlightLabel(dest, { fleet = [], locations = [], routes = [] } = {}) {
  if (!dest) return "—";
  if (dest.time === "Hub") return "Hub";
  const manualMin = parseInt(dest.time) || 0;
  if (manualMin > 0) return "~" + manualMin + " min";
  const calcMin = getApproxFlightMin("Athens", dest.name, { fleet, locations, routes });
  if (calcMin > 0) return "~" + calcMin + " min";
  return "—";
}
